"use client"

import { DEFAULT_CAPTION_STYLE_CONFIG } from "@/remotion/schemas"
import type { CaptionStyleConfig } from "@/remotion/schemas"

/** 2–3 word caption groups read at Opus size: a 58px preset renders ~87px (4.5% of a 1920px frame). */
export const CAPTION_RENDER_SCALE = 1.5
export const CAPTION_MIN_RENDER_PX = 48
export const CAPTION_MAX_RENDER_PX = 100

export type CaptionDisplayMode = "preview" | "render"

export interface CaptionDisplayContext {
  mode: CaptionDisplayMode
  compositionWidth?: number
  compositionHeight?: number
  wordsInGroup?: number
  hasLongWord?: boolean
}

interface CaptionBackdropDisplay {
  enabled: boolean
  color: string
  paddingX: string
  paddingY: string
  radius: number
}

export interface CaptionDisplayModel {
  style: CaptionStyleConfig
  fontSize: number
  strokeWidth: number
  shadowBlur: number
  lineHeight: number
  letterSpacing: string
  captionWidthPct: number
  defaultColor: string
  primaryColor: string
  secondaryColor: string
  activeTextColor: string
  activeBackgroundColor: string
  textShadow: string
  backdrop: CaptionBackdropDisplay
}

const LOW_CONTRAST_DARKS = new Set([
  "#000",
  "#000000",
  "black",
  "#0a0a0a",
  "#101010",
  "#111111",
  "#121212",
  "#171717",
  "#1a1a1a",
  "#1f1f1f",
  "#222222",
  "#2a2a2a",
])

function mergeDeep<T extends Record<string, unknown>>(base: T, partial?: Partial<T> | null): T {
  if (!partial) return { ...base }
  const result = { ...base } as Record<string, unknown>

  for (const key of Object.keys(partial) as Array<keyof T>) {
    const value = partial[key]
    if (
      value !== undefined &&
      value !== null &&
      typeof value === "object" &&
      !Array.isArray(value)
    ) {
      const current = result[key as string]
      result[key as string] = mergeDeep(
        (typeof current === "object" && current !== null ? current : {}) as Record<string, unknown>,
        value as Record<string, unknown>,
      )
    } else if (value !== undefined) {
      result[key as string] = value
    }
  }

  return result as T
}

export function resolveCaptionStyleConfig(style?: Partial<CaptionStyleConfig> | null): CaptionStyleConfig {
  return mergeDeep<CaptionStyleConfig>(DEFAULT_CAPTION_STYLE_CONFIG, style)
}

export function pickReadableAccentColor(color?: string): string {
  const normalized = (color ?? "").trim().toLowerCase()
  if (!normalized) return "#FDE047"
  return LOW_CONTRAST_DARKS.has(normalized) ? "#FDE047" : (color ?? "#FDE047")
}

export function pickReadableTextColor(backgroundColor?: string): string {
  const normalized = (backgroundColor ?? "").trim().toLowerCase()
  if (normalized.includes("255,253,3") || normalized.includes("250,204,21") || normalized === "#fffd03" || normalized === "#facc15") {
    return "#111111"
  }
  return "#FFFFFF"
}

export function getCaptionDisplayModel(
  styleInput: Partial<CaptionStyleConfig> | CaptionStyleConfig | null | undefined,
  context: CaptionDisplayContext,
): CaptionDisplayModel {
  const style = resolveCaptionStyleConfig(styleInput)
  const wordsInGroup = context.wordsInGroup ?? style.wordsPerCaption
  const hasLongWord = context.hasLongWord ?? false

  const aspectRatio =
    context.mode === "render"
      ? (context.compositionWidth ?? 1080) / Math.max(context.compositionHeight ?? 1920, 1)
      : 1080 / 1920

  const formatScale =
    context.mode === "preview"
      ? 1
      : aspectRatio < 0.7
        ? 1
        : aspectRatio < 1
          ? 0.96
          : 0.9

  const densityScale = wordsInGroup >= 6 ? 0.9 : wordsInGroup >= 5 ? 0.94 : 1
  const rawFontSize = style.font.size * formatScale * densityScale * CAPTION_RENDER_SCALE
  const fontSize =
    context.mode === "preview"
      ? 18
      : Math.max(CAPTION_MIN_RENDER_PX, Math.min(rawFontSize, CAPTION_MAX_RENDER_PX))

  const renderStrokeWidth = style.stroke.enabled
    ? Math.max(0, Math.min(style.stroke.width, Math.min(9, rawFontSize * 0.11)))
    : 0
  const strokeWidth =
    context.mode === "preview"
      ? Math.max(0.35, Math.min(renderStrokeWidth * (18 / Math.max(rawFontSize, 1)), 0.95))
      : renderStrokeWidth

  const renderShadowBlur = style.shadow.enabled
    ? Math.max(0, Math.min(style.shadow.blur, 12))
    : 0
  const shadowBlur =
    context.mode === "preview"
      ? Math.max(0, Math.min(renderShadowBlur * 0.7, 6))
      : renderShadowBlur

  const lineHeight = Math.max(1.05, Math.min(style.lineHeight, 1.35))
  const captionWidthPct =
    style.position === "middle"
      ? (hasLongWord ? 82 : 78)
      : (hasLongWord ? 86 : 82)

  const primaryColor = pickReadableAccentColor(style.colors.primary)
  const backdropEnabled = style.backdrop.enabled
  const backdropColor = style.backdrop.color
  const activeBackgroundColor = style.animation === "karaoke-bg" ? style.colors.secondary : "transparent"
  const activeTextColor =
    style.animation === "karaoke-bg"
      ? pickReadableTextColor(activeBackgroundColor)
      : primaryColor

  return {
    style,
    fontSize,
    strokeWidth,
    shadowBlur,
    lineHeight,
    letterSpacing:
      style.textTransform === "uppercase"
        ? context.mode === "preview" ? "0.02em" : "0.012em"
        : context.mode === "preview" ? "0.01em" : "0.004em",
    captionWidthPct,
    defaultColor: style.colors.default,
    primaryColor,
    secondaryColor: style.colors.secondary,
    activeTextColor,
    activeBackgroundColor,
    textShadow: style.shadow.enabled
      ? `${style.shadow.x}px ${style.shadow.y}px ${shadowBlur}px ${style.shadow.color}`
      : "none",
    backdrop: {
      enabled: backdropEnabled,
      color: backdropColor,
      paddingX: `${context.mode === "preview" ? Math.max(8, Math.round(style.backdrop.paddingX * 0.55)) : style.backdrop.paddingX}px`,
      paddingY: `${context.mode === "preview" ? Math.max(4, Math.round(style.backdrop.paddingY * 0.55)) : style.backdrop.paddingY}px`,
      radius: style.backdrop.radius,
    },
  }
}
